import { applyTick } from "./applyTick";
import { candleBucket } from "./candleBucket";
import type { ServerBasePrice } from "services/types";
import type { CandleWithFlag } from "../model/types";

interface ApplyCandleTickParams {
  dataRef: React.MutableRefObject<(ServerBasePrice | CandleWithFlag)[]>;
  timeframeMs: number;
  price: number;
  timestamp: number;
  animatedPriceRef: React.MutableRefObject<number | null>;
  animationStartTimeRef: React.MutableRefObject<number>;
  animatingCandleIndexRef: React.MutableRefObject<number | null>;
  liveCandlePrice: React.MutableRefObject<number | null>;
  prevHighRef: React.MutableRefObject<number | null>;
  prevLowRef: React.MutableRefObject<number | null>;
  highChangedRef: React.MutableRefObject<boolean>;
  lowChangedRef: React.MutableRefObject<boolean>;
}

export function applyCandleTick({
  dataRef,
  timeframeMs,
  price,
  timestamp,
  animatedPriceRef,
  animationStartTimeRef,
  animatingCandleIndexRef,
  liveCandlePrice,
  prevHighRef,
  prevLowRef,
  highChangedRef,
  lowChangedRef,
}: ApplyCandleTickParams) {
  const candles = dataRef.current as CandleWithFlag[];
  if (!candles.length) return;

  const lastCandle = candles[candles.length - 1];
  const lastCandleBucket = candleBucket(Date.parse(lastCandle.timestamp), timeframeMs);
  const bucket = candleBucket(timestamp, timeframeMs);

  if (lastCandle.isNew && bucket > lastCandleBucket) {
    lastCandle.isNew = false;
  }

  if (bucket === lastCandleBucket) {
    prevHighRef.current = lastCandle.high;
    prevLowRef.current = lastCandle.low;
    animatedPriceRef.current = lastCandle.close;
    animationStartTimeRef.current = performance.now();
    animatingCandleIndexRef.current = candles.length - 1;
  }

  const prevLength = candles.length;
  const applied = applyTick(candles, price, timestamp, timeframeMs);
  const isNewCandle = candles.length > prevLength;

  if (isNewCandle) {
    const newCandle = candles[candles.length - 1];
    prevHighRef.current = newCandle.open;
    prevLowRef.current = newCandle.open;
    animatedPriceRef.current = newCandle.open;
    animationStartTimeRef.current = performance.now();
    animatingCandleIndexRef.current = candles.length - 1;
  }
  if (applied) {
    liveCandlePrice.current = price;
  }

  const updatedCandle = candles[candles.length - 1];
  highChangedRef.current = updatedCandle.high !== prevHighRef.current;
  lowChangedRef.current = updatedCandle.low !== prevLowRef.current;
}
